import React, { useEffect, useState, useCallback } from "react";
import axios from "../axiosConfig";
import Banner from "./components/Banner";
import Header from "./components/Header";
import UserList from "./components/UserList";
import AdminTools from "./components/AdminTools";
import CompanyCard from "./components/CompanyCard";
import FinancialModal from "./components/FinancialModal";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Main.css";

const Main = () => {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [folderCompanies, setFolderCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [taskData, setTaskData] = useState({ status: '', result: '', type: '', arg: '', details: '' });
  const [details, setDetails] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [aiPromptFields, setAiPromptFields] = useState({});
  const [financials, setFinancials] = useState([]);
  const [financialModalVisible, setFinancialModalVisible] = useState(false);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get("/api/fetch/", { withCredentials: true });
      setLoadingCompanies(false);
      setCompanies(response.data.companies);
      setAnnouncements(response.data.announcements);
      setAiPromptFields(response.data.ai_prompt_fields || {});
      setFinancials(response.data.financials || []);
      if (response.data.folder_companies) {
        setFolderCompanies(response.data.folder_companies);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  }, []);

  const fetchPendingTasks = useCallback(async () => {
    try {
      console.log("Fetching pending tasks...")
      const response = await axios.get("/api/tasks/", { withCredentials: true });
      const pendingTasks = response.data?.pending_tasks ?? [];
      const completedTasks = response.data?.completed_tasks ?? [];
      console.log(pendingTasks);
      console.log(completedTasks);

      toast.dismiss();

      completedTasks.forEach((task) => {
        if (task.result === "success") {
          toast.success(`${task.type} request for ${task.arg} successful!`);
          if (["CHECK", "PREPARE", "RELEASE"].includes(task.type)) {
            setDetails(task.details);
            setDetailsModalVisible(true);
          }
        } else if (task.result === "fail") {
          toast.warning(`${task.type} request for ${task.arg} failed.`);
        }
      });

      const taskToasts = {};
      for (const task of pendingTasks) {
        try {
          console.log(task)
          taskToasts[task.task_id] = toast.loading(`Processing ${task.type} request for ${task.arg}...`);
        } catch (err) {
          console.error(`Error creating toast for task ${task.task_id}:`, err);
        }
      }

      for (const task of pendingTasks) {
        await pollTask(task, taskToasts);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  }, [fetchCompanies]);

  const pollTask = async (task, taskToasts) => {
    while (true) {
      try {
        const taskStatusResponse = await axios.get("/api/status/", {
          params: { task_id: task.task_id },
          withCredentials: true,
        });

        const { status, result, type, arg, details } = taskStatusResponse.data;
        setTaskData({ status, result, type, arg, details });

        if (result === "success") {
          toast.update(taskToasts[task.task_id], {
            render: `${type} request for ${arg} successful!`,
            type: "success",
            isLoading: false,
            autoClose: 10000,
          });
          fetchCompanies();
          if (["CHECK", "PREPARE", "RELEASE"].includes(type)) {
            setDetails(details);
            setDetailsModalVisible(true);
          }
          if (type === "PREPARE") {
            try {
              const response = await axios.get("/api/preview/", {
                params: { file: arg },
                responseType: "blob",
                withCredentials: true,
              });
              const pdfBlob = new Blob([response.data], { type: "application/pdf" });
              const pdfUrl = URL.createObjectURL(pdfBlob);
              window.open(pdfUrl, "_blank");
            } catch (err) {
              console.error("Failed to download PDF:", err);
              toast.error("Failed to open PDF.");
            }
          }
          break;
        } else if (result === "fail") {
          toast.update(taskToasts[task.task_id], {
            render: `${type} request for ${arg} failed.`,
            type: "warning",
            isLoading: false,
            autoClose: 10000,
          });
          break;
        }
      } catch (error) {
        console.error(`Error polling task ${task.task_id}:`, error);
        toast.update(taskToasts[task.task_id], {
          render: `Error checking task ${task.task_id}.`,
          type: "error",
          isLoading: false,
          autoClose: 10000,
        });
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 1000)); // Poll every 1 second
    }
  };

  const downloadAndOpenPDF = async (arg) => {
    try {
      const response = await axios.get("/api/preview/", {
        params: { file: arg },
        responseType: "blob",
        withCredentials: true,
      });
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    } catch (err) {
      console.error("Failed to download PDF:", err);
      toast.error("Failed to open PDF.");
    }
  };

  useEffect(() => {
    fetchCompanies();
    fetchPendingTasks();
    const interval = setInterval(() => fetchCompanies(), 60000);
    return () => clearInterval(interval);
  }, [fetchCompanies, fetchPendingTasks]);

  return (
    <div className="container">
      <Banner announcements={announcements} />
      <Header onUserLoad={setUser} />
      <div className="content-container">
        {user?.is_admin && <UserList />}
        {user?.is_admin && <AdminTools fetchPendingTasks={fetchPendingTasks} folderCompanies={folderCompanies} />}

        {companies.length > 0 ? (
          [false, true].map((benchmark) => {
            const group = companies
              .filter((company) => Boolean(company.benchmark) === benchmark)
              .sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
            if (group.length === 0) return null;
            return (
              <React.Fragment key={benchmark ? "benchmark" : "main"}>
                {benchmark && (
                  <>
                    <hr className="benchmark-divider" />
                    <h2 className="benchmark-heading">
                      Benchmark Companies
                      {user?.is_management && (
                        <button
                          className="benchmark-heading-btn"
                          onClick={() => setFinancialModalVisible(true)}
                        >
                          Add/Edit Financials
                        </button>
                      )}
                    </h2>
                  </>
                )}
                {group.map((company) => (
                  <CompanyCard
                    user={user}
                    key={`${benchmark ? "benchmark" : "main"}-${company.bbg}`}
                    companyId={company.company_id}
                    ticker={company.ticker}
                    name={company.company_name_short}
                    displayName={company.company_name_full}
                    files={company.files}
                    benchmarkFiles={company.benchmark_files}
                    model={company.model}
                    benchmark={benchmark}
                    financialsTable={company.financials_table}
                    financials={financials}
                    yearEnd={company.year_end}
                    fetchCompanies={fetchCompanies}
                    fetchPendingTasks={fetchPendingTasks}
                    aiPromptFields={aiPromptFields}
                  />
                ))}
              </React.Fragment>
            );
          })
        ) : loadingCompanies ? (
          <p>Loading companies, please wait...</p>
        ) : (
          <p>No companies found.</p>
        )}

        {detailsModalVisible && (
          <div className="modal-overlay">
            <div className="details-modal-box">
              <div>
                <p>Results from request:</p>
                <div dangerouslySetInnerHTML={{ __html: details }} />
              </div>
              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => {
                  setDetailsModalVisible(false);
                  setDetails(null);
                }}>Close</button>
                {taskData.type === "PREPARE" && (
                  <button className="confirm-btn" onClick={() => downloadAndOpenPDF(taskData.arg)}>Open PDF</button>
                )}
              </div>
            </div>
          </div>
        )}

        {financialModalVisible && (
          <FinancialModal
            financials={financials}
            onClose={() => setFinancialModalVisible(false)}
            onChanged={(catalogue) => {
              setFinancials(catalogue);
              fetchCompanies();
            }}
          />
        )}

        <ToastContainer />
      </div>
    </div>
  );
};

export default Main;